import { administrationClient, unwrap, createResource } from '@/shared/api-client';

export type DiscountScope = 'ITEM' | 'BILL';
export type DiscountVenueScope = 'VENUE' | 'FRANCHISE';
export type DayOfWeek =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

export interface DiscountCode {
  id: string;
  code: string;
  normalizedCode: string;
  scope: DiscountScope;
  percentageBasisPoints?: number;
  fixedAmount?: string;
  fixedAmountCurrency?: string;
  venueScope: DiscountVenueScope;
  venueId?: string;
  active: boolean;
  discoverable: boolean;
  allowedSellableIds: string[];
  minimumSpendAmount?: string;
  startsAt?: string;
  endsAt?: string;
  validDaysOfWeek: DayOfWeek[];
  validFromTime?: string;
  validUntilTime?: string;
  redemptionCap?: number;
  redeemedUnits: number;
}

export interface DiscountCodeListResponse {
  items: DiscountCode[];
}

export interface DiscountCodeInput {
  code: string;
  scope: DiscountScope;
  percentageBasisPoints?: number;
  fixedAmount?: string;
  fixedAmountCurrency?: string;
  venueScope: DiscountVenueScope;
  venueId?: string;
  discoverable?: boolean;
  allowedSellableIds?: string[];
  minimumSpendAmount?: number;
  startsAt?: string;
  endsAt?: string;
  validDaysOfWeek?: DayOfWeek[];
  validFromTime?: string;
  validUntilTime?: string;
  redemptionCap?: number;
}

export type UpdateDiscountCodeInput = DiscountCodeInput & { active: boolean };

const listDiscountCodes = (): Promise<DiscountCodeListResponse> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/discount-codes'),
  ) as Promise<DiscountCodeListResponse>;

const getDiscountCode = (id: string): Promise<DiscountCode> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/discount-codes/{id}', {
      params: { path: { id } },
    }),
  ) as Promise<DiscountCode>;

export const createDiscountCode = (input: DiscountCodeInput): Promise<DiscountCode> =>
  unwrap(
    administrationClient.POST('/api/v1/franchise-administration/discount-codes', { body: input }),
  ) as Promise<DiscountCode>;

export const updateDiscountCode = (
  id: string,
  input: UpdateDiscountCodeInput,
): Promise<DiscountCode> =>
  unwrap(
    administrationClient.PUT('/api/v1/franchise-administration/discount-codes/{id}', {
      params: { path: { id } },
      body: input,
    }),
  ) as Promise<DiscountCode>;

/** DiscountCodes routed through the shared resource factory (ADR 0012). */
const discountCodes = createResource({
  key: ['discount-codes'],
  list: listDiscountCodes,
  get: getDiscountCode,
  create: createDiscountCode,
  update: ({ id, input }: { id: string; input: UpdateDiscountCodeInput }) =>
    updateDiscountCode(id, input),
});

export const useDiscountCodes = discountCodes.useList;
export const useDiscountCode = discountCodes.useDetail;
export const useCreateDiscountCode = discountCodes.useCreate;
export const useUpdateDiscountCode = discountCodes.useUpdate;
